import styled from 'styled-components';

// Shared styled-components for the score-spec editor. Extracted here (rather than left in
// SpecEditor.js) now that four files in this directory need them.
//
// The token split is deliberate and consistent across the feature: an INTERACTIVE surface the
// operator picks or edits uses `border`; a PASSIVE panel that only tells them something uses
// `lowContrastBorder`.

export const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 7px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

export const PassiveCard = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 7px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  padding: 16px;
`;

export const ReadOnlyJson = styled.pre`
  margin: 0;
  padding: 12px 16px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 12.5px;
`;

// 16px between components is the house rhythm; Row is the horizontal expression of it.
// Vertical stack of sibling components — kind cards, rule rows.
export const Stack = styled.div`
  display: grid;
  gap: 16px;
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: ${({ $align }) => $align || 'center'};
`;

export const FieldGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

// Small uppercase section marker. Used for the sub-headings inside a question panel, where a
// full Body5 heading would out-shout the panel title.
export const MicroLabel = styled.div`
  font-size: 10.5px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-family: monospace;
  border-radius: 5px;
  padding: 2px 8px;
  border: 1px solid
    ${({ theme, $tone }) => {
      if ($tone === 'bad') return theme.themeColors.error;
      if ($tone === 'good') return theme.themeColors.positive;
      if ($tone === 'warn') return theme.themeColors.warning;
      return theme.themeColors.border;
    }};
  color: ${({ theme, $tone }) => {
    if ($tone === 'bad') return theme.themeColors.error;
    if ($tone === 'good') return theme.themeColors.positive;
    if ($tone === 'warn') return theme.themeColors.warning;
    return theme.themeColors.secondaryText;
  }};
`;

export const Consequence = styled.div`
  font-size: 11.5px;
  line-height: 1.5;
  color: ${({ theme, $tone }) => {
    if ($tone === 'bad') return theme.themeColors.error;
    if ($tone === 'warn') return theme.themeColors.warning;
    return theme.themeColors.secondaryText;
  }};
`;

// The compact two-or-three-way switch that sits inside a panel: Form vs `{ }` in a question panel,
// and which JSON a spec's viewer is showing. Lives here rather than in QuestionPanel because a
// second caller needed the same chrome for the same reason — a mode choice small enough not to
// out-shout the panel it sits in.
//
// Styling hangs off `aria-pressed` rather than a prop, so the accessible state and the visible one
// cannot drift apart: a caller that forgets the attribute gets a switch with no lit segment.
export const ModeToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 5px;
  overflow: hidden;

  button {
    border: 0;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
    color: ${({ theme }) => theme.themeColors.secondaryText};
    font-size: 11px;
    font-family: monospace;
    padding: 3px 9px;
    cursor: pointer;
  }

  button[aria-pressed='true'] {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};
    color: ${({ theme }) => theme.themeColors.buttonText};
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -2px;
  }
`;
